import axios from 'axios';
import { getTenantSlug } from '../utils/tenant';

const API_URL = import.meta.env.VITE_API_URL as string;

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is required');
}

export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send HttpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach access token + tenant slug to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('attendza_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always send the current tenant slug so the backend can resolve tenant_id.
    // The slug is read fresh on each request so a login-page tenant change takes
    // effect immediately without needing to recreate the axios instance.
    const slug = getTenantSlug();
    if (slug) {
      config.headers['X-Tenant-Slug'] = slug;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

/**
 * Reads the error `code` out of a response body.
 *
 * A request made with `responseType: 'blob'` (every file download) delivers even a
 * JSON error body as a Blob, so `data.code` is undefined. Without this, the refresh
 * below never recognised TOKEN_EXPIRED on a download: the grid CSV and
 * /api/reports/export both failed outright once the 15-minute access token expired,
 * while every JSON call on the same page silently refreshed and carried on.
 * Blobs are immutable, so reading the text here does not consume it.
 */
async function readErrorCode(data: unknown): Promise<string | undefined> {
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { code?: string };
      return parsed.code;
    } catch {
      return undefined; // not a JSON error body
    }
  }
  return (data as { code?: string } | undefined)?.code;
}

// On 401 TOKEN_EXPIRED attempt silent refresh once
apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    const code = await readErrorCode(error.response?.data);

    if (error.response?.status === 401 && code === 'TOKEN_EXPIRED' && !originalRequest?._retry) {
      if (originalRequest) originalRequest._retry = true;
      try {
        const { data } = await axios.post(
          `${API_URL}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const newToken = data.data?.accessToken as string;
        localStorage.setItem('attendza_token', newToken);
        if (originalRequest?.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return apiClient(originalRequest!);
      } catch {
        localStorage.removeItem('attendza_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // The server refuses everything but a password change while the password is temporary or
    // expired. Whatever page asked, take the person to the one screen that works.
    if (error.response?.status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
      const target = '/account/security/password';
      if (!window.location.pathname.startsWith(target)) {
        window.location.assign(`${target}?expired=1`);
      }
    }

    return Promise.reject(error);
  }
);
